import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import { Route, Switch, match as Match } from 'react-router-dom'
import { Redirect } from 'react-router'
import { useToasts } from 'react-toast-notifications'

import { BottomNavigationLinks } from 'src/legacy/components/mui'
import { ConversationChatIcon, EnvelopeIcon, MedicalNotesIcon } from 'src/legacy/components/Icon'

import { ParticipantFeedCombined } from 'src/feed'
import ParticipantRecords from 'src/legacy/components/ParticipantRecords'
import { VideoCallBookingHeader } from 'src/videoCall'
import { useLoadAllParticipantData } from 'src/participant'
import { MonthlySummaryNotificationProvider } from 'src/notifications/MonthlySummaryNotificationProvider'
import ParticipantEmails from '../components/ParticipantEmails/ParticipantEmails'

export type Props = {
  match: Match<{ uid: string }>
  uid: string
}

const BOTTOM_NAVIGATION_HEIGHT = 56

const ContentContainer: React.FunctionComponent<{ uid: string }> = ({ children, uid }) => (
  <Box height="100%" pb={`${BOTTOM_NAVIGATION_HEIGHT}px`}>
    {children}
    <BottomNavigationLinks
      actions={[
        {
          icon: <ConversationChatIcon />,
          label: 'Feed',
          to: `/participant/${uid}`,
        },
        {
          icon: <MedicalNotesIcon />,
          label: 'Records',
          to: `/participant/${uid}/records`,
        },
        {
          icon: <EnvelopeIcon />,
          label: 'Emails',
          to: `/participant/${uid}/emails`,
        },
      ]}
    />
  </Box>
)

const Participant: React.FunctionComponent<Props> = ({ match, uid }) => {
  const recordsPath = `${match.url}/records`
  const feedPath = match.url
  const result = useLoadAllParticipantData(uid)
  const { addToast } = useToasts()

  useEffect(() => {
    if (result.status === 'failure') {
      addToast(
        "Failed to retrieve the participant's information. Make sure that you have the required access for this participant",
        {
          appearance: 'error',
        }
      )
    }
  }, [result.status, addToast])

  if (result.status === 'failure') {
    return <Redirect to="/" />
  }

  return (
    <MonthlySummaryNotificationProvider uid={uid}>
      <ContentContainer uid={uid}>
        <Box display="flex" flexDirection="column" height="100%">
          <Route path={[recordsPath, feedPath]} exact>
            <Box flexGrow={0} zIndex={1}>
              <VideoCallBookingHeader uid={uid} participantWithFullInformation={result.patient} />
            </Box>
          </Route>

          <Box flexGrow={1} position="relative">
            <Switch>
              <Route path={`${match.url}/emails`}>
                <ParticipantEmails uid={uid} />
              </Route>
              <Route path={recordsPath}>
                <ParticipantRecords uid={uid} isLoading={result.status === 'loading'} patient={result.patient} />
              </Route>
              <Route exact path={feedPath}>
                <ParticipantFeedCombined feedID={uid} />
              </Route>
            </Switch>
          </Box>
        </Box>
      </ContentContainer>
    </MonthlySummaryNotificationProvider>
  )
}

export default Participant
